import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { Reveal } from "./anim";

const QUOTES = [
  {
    quote:
      "Quadrim rebuilt our infrastructure without a single minute of downtime across 40 clinics. They just quietly made everything faster and safer.",
    role: "COO, Meridian Health",
  },
  {
    quote:
      "The custom logistics platform they shipped handles millions of events a day. It's the most reliable software we've ever put into production.",
    role: "VP Engineering, Halcyon Logistics",
  },
  {
    quote:
      "They think like owners. Every recommendation was about our business outcomes, not billing more hours. Rare, and worth every penny.",
    role: "CEO, Northwind Retail",
  },
  {
    quote:
      "From a chaotic in-house setup to a calm, monitored, secure stack. Quadrim became the IT department we always wished we had.",
    role: "Director of Ops, Atlas Manufacturing",
  },
  {
    quote:
      "Their Cisco VoIP rollout across our branches was flawless — numbers ported overnight and not one dropped call the next morning.",
    role: "IT Director, Vantage Financial",
  },
  {
    quote:
      "Response times are unreal. Whenever we raise a ticket, a real engineer who knows our setup is on it within minutes.",
    role: "Practice Manager, Lakeside Dental Group",
  },
  {
    quote:
      "Quadrim modernised our systems across three campuses on time and on budget. Our staff barely noticed the switchover — which is the point.",
    role: "CTO, BrightPath Education",
  },
];

export default function Testimonials() {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);

  const go = useCallback((n) => {
    setDir(n > i ? 1 : -1);
    setI((n + QUOTES.length) % QUOTES.length);
  }, [i]);

  const next = useCallback(() => { setDir(1); setI((p) => (p + 1) % QUOTES.length); }, []);
  const prev = () => { setDir(-1); setI((p) => (p - 1 + QUOTES.length) % QUOTES.length); };

  useEffect(() => {
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next]);

  const active = QUOTES[i];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative bg-[#0A0A0B] border-t border-white/10 py-24 md:py-32 px-6 md:px-10 overflow-hidden"
    >
      <Quote
        className="pointer-events-none absolute -top-6 left-4 md:left-10 h-40 w-40 md:h-72 md:w-72 text-white/[0.03]"
        aria-hidden="true"
      />
      <div className="mx-auto max-w-[1200px] relative">
        <Reveal as="p" className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-12 md:mb-16">
          [ 05 ] — Client voices
        </Reveal>

        <div className="min-h-[280px] md:min-h-[240px] flex items-center">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.blockquote
              key={i}
              data-testid="testimonial-quote"
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-light leading-[1.2] tracking-tight text-[#F4F4F5]">
                "{active.quote}"
              </p>
              <footer className="mt-8 flex items-center gap-4">
                <span className="h-px w-10 bg-accent" />
                <span className="block font-mono text-sm uppercase tracking-[0.12em] text-[#A1A1AA]">
                  {active.role}
                </span>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="mt-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {QUOTES.map((_, idx) => (
              <button
                key={idx}
                data-testid={`testimonial-dot-${idx}`}
                onClick={() => go(idx)}
                aria-label={`Show testimonial ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === i ? "w-8 bg-accent" : "w-4 bg-white/15 hover:bg-white/30"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="testimonial-prev"
              onClick={prev}
              aria-label="Previous testimonial"
              className="grid h-12 w-12 place-items-center rounded-full border border-white/15 text-[#A1A1AA] hover:border-accent hover:text-white transition-colors duration-300"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="testimonial-next"
              onClick={next}
              aria-label="Next testimonial"
              className="grid h-12 w-12 place-items-center rounded-full border border-white/15 text-[#A1A1AA] hover:border-accent hover:text-white transition-colors duration-300"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
